import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme, THEMES } from '@/context/ThemeContext';
import { NAV_LINKS, TYPEFORM_URL } from '@/data/mock';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Menu, X, Sun, Moon, Sparkles,
  HeartPulse, HeartHandshake, MessageSquare,
  Crown, Brain, Stethoscope, Shield
} from 'lucide-react';

const iconMap = {
  HeartPulse, HeartHandshake, MessageSquare,
  Crown, Brain, Stethoscope, Shield
};

const themeIcons = { sun: Sun, moon: Moon, sparkles: Sparkles };

export default function Header() {
  const { currentTheme, cycleTheme, theme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const t = currentTheme;
  const nextThemeKey = Object.keys(THEMES)[(Object.keys(THEMES).indexOf(theme) + 1) % Object.keys(THEMES).length];
  const NextIcon = themeIcons[THEMES[nextThemeKey].icon];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? t.navBg : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${t.borderColor}` : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src={theme === 'light'
                ? 'https://customer-assets.emergentagent.com/job_motivy-light/artifacts/1jtvp4vu_LOGO-FINAL-MOTIVY%20%281%29%20%281%29.png'
                : 'https://customer-assets.emergentagent.com/job_motivy-light/artifacts/y1xqcc0j_logo%20blanco%20%281%29.png'
              }
              alt="Motivy"
              className="h-8 lg:h-10"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                <div key={link.label} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                    style={{ color: t.navText }}
                    onMouseEnter={() => setDropdownOpen(true)}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {dropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-72 rounded-xl p-2 border"
                        style={{
                          background: t.cardBg,
                          borderColor: t.borderColor,
                          boxShadow: `0 20px 40px ${t.shadowColor}`,
                          backdropFilter: theme === 'aurora' ? 'blur(20px)' : 'none',
                        }}
                        onMouseLeave={() => setDropdownOpen(false)}
                      >
                        {link.items.map((item) => {
                          const Icon = iconMap[item.icon];
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200"
                              style={{ color: t.text }}
                              onMouseEnter={(e) => e.currentTarget.style.background = t.surfaceHover}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              <div
                                className="w-9 h-9 rounded-lg flex items-center justify-center"
                                style={{ background: t.featureIconBg }}
                              >
                                {Icon && <Icon size={18} style={{ color: t.featureIconColor }} />}
                              </div>
                              <span className="text-sm font-medium">{item.label}</span>
                            </Link>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  style={{
                    color: location.pathname === link.path ? t.accent : t.navText,
                  }}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={cycleTheme}
              className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-200"
              style={{ background: t.featureIconBg, color: t.featureIconColor }}
              title={`Cambiar a tema ${THEMES[nextThemeKey].label}`}
            >
              <NextIcon size={18} />
            </button>

            {/* CTA */}
            <a
              href={TYPEFORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-200"
              style={{ background: t.accent }}
              onMouseEnter={(e) => e.currentTarget.style.background = t.accentHover}
              onMouseLeave={(e) => e.currentTarget.style.background = t.accent}
            >
              Solicitar demo
            </a>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center"
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{ color: t.navText }}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t overflow-hidden"
            style={{
              background: t.navBg,
              backdropFilter: 'blur(20px)',
              borderColor: t.borderColor,
            }}
          >
            <div className="px-4 py-4 space-y-1">
              {NAV_LINKS.map((link) =>
                link.dropdown ? (
                  <div key={link.label}>
                    <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider" style={{ color: t.textMuted }}>
                      Soluciones
                    </p>
                    {link.items.map((item) => {
                      const Icon = iconMap[item.icon];
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                          style={{ color: t.text }}
                        >
                          {Icon && <Icon size={16} style={{ color: t.featureIconColor }} />}
                          <span className="text-sm">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block px-3 py-2.5 rounded-lg text-sm font-medium"
                    style={{ color: t.text }}
                  >
                    {link.label}
                  </Link>
                )
              )}
              <div className="pt-3">
                <a
                  href={TYPEFORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center px-5 py-3 rounded-lg text-sm font-semibold text-white"
                  style={{ background: t.accent }}
                >
                  Solicitar demo
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
